import { sameCompanyFaq } from "@/lib/brand";
import { createFileRoute } from "@tanstack/react-router";
import { hourlyMinimumFaq } from "@/lib/booking-minimums";
import { BEFORE_YOU_BOOK, SAFETY_FACTS, WHY_NOT_CHEAPEST } from "@/lib/safety-facts";
import SeoLandingPage, {
  buildFaqSchema,
  buildLocalBusinessSchema,
} from "@/components/SeoLandingPage";
import heroImage from "@/assets/sprinter-starlight-blue.jpg?format=webp&quality=72&w=1400&imagetools";
import midImage from "@/assets/sprinter-limo-2.jpg?format=webp&quality=72&w=1400&imagetools";
import galleryA from "@/assets/sprinter-limo-1.jpg?format=webp&quality=72&w=1400&imagetools";
import galleryB from "@/assets/sprinter-exec-green.jpg?format=webp&quality=72&w=1400&imagetools";
import galleryC from "@/assets/limo-style-sprinter-party-bus-lounge-bar.jpg?format=webp&quality=72&w=1400&imagetools";
import brideVeil from "@/assets/wedding-bride-veil-celebration-atlanta.jpg?format=webp&quality=72&w=800&imagetools";

const PAGE_URL = "https://atlantaluxurysprinters.com/wedding-transportation-atlanta";
const PAGE_TITLE = "Wedding Transportation Atlanta | Sprinter Limo Service";
const PAGE_DESC =
  "Chauffeured Mercedes-Benz Sprinter Limousines for Atlanta weddings, bridal party transfers, the couple's private ride, and guest loops to the hotel block. Call (678) 431-7222.";

const SAFETY = "Safety & Licensing";
const PRICING = "Planning & Booking";
const THE_DAY = "On the Wedding Day";

const faqs = [
  {
    group: SAFETY,
    q: "Are you licensed and insured?",
    a: "Yes. We are registered with the State of Georgia, operate 100% legally and carry commercial insurance. Every chauffeur is fingerprinted and background-checked, and our vehicles have cameras and AI driver-safety technology. Ask any wedding transportation company for the same before you pay a deposit.",
  },
  {
    group: PRICING,
    q: "How far in advance should we book wedding transportation in Atlanta?",
    a: "For Saturdays in April through June or September through November, plan six to nine months ahead. Off-peak weekends and weekdays are usually available within 60 days. Text me once your date is set, before you finalize the hotel block, because guest count decides how many vehicles you need.",
  },
  {
    group: PRICING,
    q: "How many people fit in a Mercedes-Benz Sprinter Limousine for a wedding?",
    a: "Twelve to fourteen passengers in wraparound leather lounge seating, with a separate rear cargo bay for dress bags, shoes, and gifts. Gowns are the reason most couples choose the Sprinter over a traditional stretch: the cabin is tall enough to stand in and the step-in is low, so nobody crushes a dress climbing in.",
  },
  hourlyMinimumFaq({ group: PRICING }),
  sameCompanyFaq({ group: PRICING }),
  {
    group: THE_DAY,
    q: "Can one vehicle cover the whole wedding day?",
    a: "Usually. One hourly booking can cover getting ready, the ceremony, your private transfer and the reception. A second vehicle handles hotel guest trips while the first stays with your wedding party.",
  },
  {
    group: THE_DAY,
    q: "Do you handle guest transportation between the hotel block and the venue?",
    a: "Yes. We follow your planner's schedule, typically three to four runs before the ceremony, staggered returns after the last dance and a final guest sweep.",
  },
  {
    group: THE_DAY,
    q: "What happens if the ceremony runs late?",
    a: "Your chauffeur stays for the hourly block, and your planner can call them directly. Overtime uses the same rate and billing increments, with no repositioning fee.",
  },
  {
    group: THE_DAY,
    q: "Can we decorate the vehicle or bring champagne?",
    a: "Guests 21+ may bring champagne; we provide glassware and ice, but we don't sell alcohol. Exterior decorations can't use adhesive on paint or glass. Magnetic signs and mirror ribbons work well.",
  },
  {
    group: THE_DAY,
    q: "Do you serve venues outside the perimeter?",
    a: "Yes. Chateau Elan in Braselton, Foxhall Resort in Douglasville, Dahlonega and North Georgia wine country, Serenbe and Blue Ridge cabin weddings are regular routes. These venues use a full-day block rather than point-to-point service.",
  },
];

export const Route = createFileRoute("/wedding-transportation-atlanta")({
  head: () => ({
    meta: [
      { title: PAGE_TITLE },
      { name: "description", content: PAGE_DESC },
      {
        name: "keywords",
        content:
          "wedding transportation atlanta, atlanta wedding limo, wedding limo service atlanta, bridal party transportation atlanta, sprinter limo wedding atlanta, wedding car service atlanta",
      },
      { property: "og:title", content: PAGE_TITLE },
      { property: "og:description", content: PAGE_DESC },
      { property: "og:type", content: "website" },
      { property: "og:url", content: PAGE_URL },
      { property: "og:image", content: heroImage },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: PAGE_TITLE },
      { name: "twitter:description", content: PAGE_DESC },
      { name: "twitter:image", content: heroImage },
    ],
    links: [{ rel: "canonical", href: PAGE_URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(
          buildLocalBusinessSchema({
            url: PAGE_URL,
            name: "Atlanta Luxury Sprinters, Wedding Transportation Atlanta",
            image: heroImage,
            description: PAGE_DESC,
            serviceType: "Wedding Transportation",
            areaServed: [
              { type: "City", name: "Atlanta" },
              { type: "City", name: "Buckhead" },
              { type: "City", name: "Alpharetta" },
              { type: "City", name: "Braselton" },
            ],
          }),
        ),
      },
      { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(faqs)) },
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <SeoLandingPage
      eyebrow="Weddings • Metro Atlanta"
      h1="Wedding Transportation in Atlanta"
      intro="You've planned every detail. Let me take the driving off your list: the bridal party, your private ride and guest trips between the hotel and venue, in our Mercedes-Benz Sprinter Limousines."
      heroImage={heroImage}
      heroAlt="Mercedes-Benz Sprinter Limousine interior with starlight headliner set for an Atlanta wedding party"
      midImage={midImage}
      midAlt="Wedding party seating inside a Mercedes-Benz Sprinter Limousine in Atlanta"
      sectionOneTitle="Your Day, Planned With Your Planner"
      sectionOneBody={[
        "I know what you're trusting us with. We plan backward from your ceremony time, stage early and give your planner direct contact with your chauffeur.",
        "Your chauffeur stays for the booked block. The Sprinter's tall, wide cabin gives gowns, suits and hair room between venues.",
        "Atlanta Luxury Sprinters is the luxury Sprinter fleet of Empress Elite Limousine, my family's company.",
      ]}
      bulletsTitle="What Your Wedding Booking Includes"
      bulletsIntro="Your hourly booking keeps one chauffeur with your day, from the first pickup to the last return."
      bullets={[
        "Dedicated chauffeur for the full block, no vehicle swaps mid-day",
        "Direct chauffeur contact for your planner or coordinator",
        "Vehicle staged 30 minutes before the first scheduled movement",
        "Separate rear cargo bay for dress bags, gifts, and shoes",
        "Wet bar with ice wells and chilled storage, bring your own champagne",
        "Starlight headliner and color-selectable cabin lighting for photos",
        "Chauffeur in suit and tie, vehicle detailed and photographed before every trip",
        "Fingerprinted, background-checked chauffeurs and commercial insurance on every ride",
      ]}
      coverageTitle="Atlanta Wedding Venues and Areas We Serve"
      coverageIntro="These are regular routes for my chauffeurs, from Buckhead hotels to North Georgia venues."
      coverage={[
        "Buckhead",
        "Midtown Atlanta",
        "Downtown Atlanta",
        "Sandy Springs",
        "Alpharetta & Avalon",
        "Roswell & Milton",
        "Serenbe",
        "Chateau Elan, Braselton",
        "Dahlonega wine country",
        "Blue Ridge",
        "Foxhall Resort, Douglasville",
        "Athens, GA",
      ]}
      routeTable={{
        title: "Common Atlanta Wedding Day Routes",
        intro:
          "Drive times from our own trip history, so your planner can build a timeline that actually holds on a Saturday afternoon.",
        rows: [
          {
            route: "Buckhead hotel block → in-town ceremony venue",
            distance: "5–15 mi",
            time: "20–40 min",
            notes: "Guest loops run 3–4 times before the ceremony; Peachtree Rd staging confirmed in advance.",
          },
          {
            route: "Getting-ready suite → ceremony (same neighborhood)",
            distance: "2–6 mi",
            time: "10–20 min",
            notes: "Vehicle staged 30 min early; formalwear loading takes longer than the drive.",
          },
          {
            route: "Ceremony → reception (couple's private transfer)",
            distance: "3–12 mi",
            time: "15–30 min",
            notes: "The quiet twenty minutes of the day. Route padded so you are never rushed.",
          },
          {
            route: "Atlanta → Chateau Elan, Braselton",
            distance: "~45 mi",
            time: "50–75 min",
            notes: "I-85 N. Full-day block; Friday afternoon departures need an extra 30 min.",
          },
          {
            route: "Atlanta → Serenbe (Chattahoochee Hills)",
            distance: "~32 mi",
            time: "45–60 min",
            notes: "I-85 S to GA-154. Limited parking on site, staged drop point arranged with the venue.",
          },
          {
            route: "Atlanta → Foxhall Resort, Douglasville",
            distance: "~35 mi",
            time: "45–65 min",
            notes: "I-20 W. Gravel approach road; we stage at the main pavilion entrance.",
          },
          {
            route: "Atlanta → Dahlonega / North Georgia venues",
            distance: "~65 mi",
            time: "75–95 min",
            notes: "GA-400 N. Mountain grades on the return, allow extra after dark.",
          },
          {
            route: "Reception → hotel block, late-night returns",
            distance: "5–15 mi",
            time: "20–40 min",
            notes: "Staggered returns plus a final sweep after last dance so no guest is stranded.",
          },
        ],
        footnote:
          "Hourly bookings keep the vehicle and chauffeur with you between every movement, including waiting time at the venue.",
      }}
      detailSections={[
        {
          title: "A Wedding Day Timeline That Holds",
          body: [
            "Wedding transportation fails on timing, not on vehicles. This is the structure we run for Atlanta weddings, adjusted to whatever your planner's schedule requires.",
          ],
          list: [
            "Vehicle staged at the getting-ready location 30 minutes before the first movement.",
            "Bridal party transfer to the ceremony venue, arriving 45 minutes before ceremony start.",
            "Couple's private transfer between ceremony and reception, routed long on purpose.",
            "Guest transfer loops between hotel block and reception across the evening.",
            "Late-night departures and hotel returns after last dance, with a final sweep of the venue.",
          ],
        },
        {
          title: "How Many Vehicles Do You Actually Need?",
          body: [
            "Most Atlanta couples over-order or under-order by exactly one vehicle. The honest math depends on whether guests are moving, not on total headcount.",
          ],
          list: [
            "Wedding party only (up to 14), one Mercedes-Benz Sprinter Limousine for the full day.",
            "Wedding party plus parents and grandparents, add a Suburban SUV for a shorter, quieter ride.",
            "Guest block under 60 with a nearby venue, one Sprinter running timed loops covers it.",
            "Guest block over 60, or a venue more than 25 minutes out, two vehicles, staged departures.",
            "Couple's exit only, a single Executive Sprinter for two hours, staged at the reception.",
          ],
        },
        {
          title: "Licensed, Insured and Careful About Who Drives",
          body: [
            "Your wedding party, your parents and your grandparents will all ride with us. Safety, comfort and reliability matter as much on a wedding day as the look of the vehicle.",
            WHY_NOT_CHEAPEST,
          ],
          list: SAFETY_FACTS,
        },
        {
          title: "Working With Your Planner",
          body: [
            "We take the timeline from your planner or coordinator in whatever form they already use, and we confirm it back as vehicle movements with staging times attached. On the day, your planner has the chauffeur's mobile number directly.",
            "If the ceremony slips, nothing needs renegotiating, the block simply extends at the same hourly rate. If the photographer wants ten extra minutes with the couple in the cabin, the vehicle stays.",
          ],
        },
      ]}
      gallery={{
        title: "The Wedding Fleet",
        intro:
          "Mercedes-Benz Sprinter Limousines with starlight headliners, plus Executive Sprinters for family and parent transfers.",
        images: [
          { src: galleryA, alt: "Mercedes-Benz Sprinter Limousine lounge seating for an Atlanta wedding party", caption: "Sprinter Limousine, 12–14 passengers" },
          { src: galleryB, alt: "Executive Sprinter cabin with facing captain's chairs for wedding family transfers", caption: "Executive Sprinter, parents and family" },
          { src: galleryC, alt: "Sprinter Limousine lounge interior with ambient lighting", caption: "Ambient lighting set for photos" },
        ],
      }}
      reviews={{
        title: "From Couples and Families We've Driven",
        intro: "Wedding days and wedding weekends, in our clients' own words.",
        photos: [
          { src: brideVeil, alt: "Bride in her veil celebrating with guests inside the cabin on her wedding day" },
        ],
        photoCredit: "Wedding day photo shared by Erika B. in her Google review: \"Great service, great car, great customer service! I loved 🥰, we loved 💙\"",
        items: [
          {
            name: "Zeynep O.",
            date: "September 2023",
            text: "DONT HESITATE TO BOOK!!! Booking [transportation] for our wedding was one of the most stressful vendors for me … I switched to empress elite last minute for my transportation & I am SO glad I did! … Customer service was top notch from the start.",
          },
          {
            name: "Stephen M.",
            date: "June 2023",
            text: "Thank you Frederic and Empress for the help over the weekend with transportation at Berry College for our son's wedding!",
          },
          {
            name: "Nick D.",
            date: "July 2021",
            text: "The service was top notch! We used Empress Elite while we were in town for a family wedding and they drove us to and from the airport. Both drivers were on time and incredibly friendly. The cars were very spacious and CLEAN! I would HIGHLY recommend using this car service!!",
          },
          {
            name: "Katia E.",
            date: "February 2019",
            text: "I highly recommend Empress Elite Limousine, their cars, drivers and owner are the best. … we had them drop us off and pick us up for our wedding and other special events. They are always on time, waiting to grab your bag and or open the door.",
          },
        ],
      }}
      faqs={faqs}
      faqTitle="Atlanta Wedding Transportation FAQs"
      relatedTitle="Related Occasion Services"
      related={[
        { to: "/concert-sports-transportation-atlanta", title: "Concert & Sports Transportation", desc: "Mercedes-Benz Stadium, State Farm Arena, and Truist Park." },
        { to: "/winery-tour-transportation-atlanta", title: "Winery & Brewery Tours", desc: "Dahlonega and North Georgia wine trail day trips." },
        { to: "/sprinter-limo-atlanta", title: "Sprinter Limo Atlanta", desc: "The full Mercedes-Benz Sprinter Limousine fleet." },
      ]}
      ctaTitle="Text Me Your Wedding Date"
      ctaBody="Tell me your date, venues and group size. I'll check availability myself and send your quote in writing."
    />
  );
}
